import { read_contact_display_payload, read_contact_patch } from '../../utils/contact/contact_access';
import { run_with_concurrent_work_confirmation } from "../../utils/concurrent_work_confirmation";
import { run_with_shared_contact_confirmation } from "../../utils/shared_contact_confirmation";
import axios from "axios";
import { read_client_label_after_sales_update } from "../../utils/client_lifecycle";

import { config } from "../../config";
import { read_brand_input_value } from "../../utils/input/brand_input";
import { read_quest_provider_counts } from "../../utils/quest/quest_provider_counts";

const post_with_concurrent_confirmation = (url, body) => run_with_concurrent_work_confirmation(confirmation => {
    return axios.post(url, confirmation ? { ...body, concurrentWorkConfirmation: confirmation } : body);
});
const prospectBacklogStatus = "Backlog";
const prospectAssignedStatus = "To Call";
const prospectFinalSalesStatuses = ["Agency", "Sass", "Contract Sent", "Lost"];
const requestTimeout = 15000;
const pageRequestTimeout = 30000;
const searchRequestTimeout = 15000;
const salesPerformanceCacheTtl = 5000;
const salesPerformanceCache = new Map();

/**
 * Reads the rough serialized request size used by browser debug logs.
 */
const read_json_size = (value = {}) => {
    return JSON.stringify(value || {}).length;
};

/**
 * Reads sanitized Sales request state without logging names, emails, or phones.
 */
const read_sales_api_debug_payload = (row = {}, brand = {}) => {
    return {
        prospectId: row?.prospectId || "",
        brandId: row?.brandId || row?._id || brand?._id || "",
        status: brand?.status || row?.status || "",
        rowStatus: row?.status || "",
        inSales: brand?.inSales === true,
        rowInSales: row?.inSales === true,
        hasBrandName: Boolean(`${brand?.name || row?.brand || ""}`.trim()),
        clientBrand: brand?.client === true || row?.client === true || row?.brandRecord?.client === true,
        hasActiveSalesSession: Boolean(brand?.activeSalesSession?.sessionId || row?.activeSalesSession?.sessionId || row?.brandRecord?.activeSalesSession?.sessionId),
        commentsCount: Array.isArray(brand?.comments) ? brand.comments.length : 0,
        historyCount: Array.isArray(brand?.history) ? brand.history.length : 0,
        legacyTimelineCount: Array.isArray(brand?.legacy_timeline) ? brand.legacy_timeline.length : 0,
        timelineCount: Array.isArray(brand?.timeline) ? brand.timeline.length : 0,
        tagsCount: Array.isArray(brand?.tags) ? brand.tags.length : 0,
        payloadCharacters: read_json_size(brand)
    };
};

/**
 * Reads a compact backend error object for browser debugging.
 */
const read_sales_api_error_payload = (error = {}) => {
    return {
        status: error?.response?.status || 0,
        statusText: error?.response?.statusText || "",
        message: error?.message || "",
        backendMessage: error?.response?.data?.message || error?.response?.data?.msg || "",
        backendCode: error?.response?.data?.code || "",
        timeout: error?.code === "ECONNABORTED"
    };
};

/**
 * Reads a compact backend response object for browser debugging.
 */
const read_sales_api_response_payload = (responseData = {}) => {
    const responseProspect = read_contact_display_payload(responseData?.prospect || responseData);

    return {
        success: Boolean(responseProspect?._id),
        blocked: responseData?.blocked === true,
        prospectId: responseProspect?._id || "",
        brandsCount: Array.isArray(responseProspect?.brands) ? responseProspect.brands.length : 0,
        syncWarning: responseData?.sync?.warning || "",
        message: responseData?.message || responseData?.msg || ""
    };
};

/**
 * Keeps prospect callers compatible while preserving cross-application sync metadata.
 */
export const read_prospect_response = (responseData = {}) => {
    if (responseData?.blocked) {
        return {
            blocked: true,
            reason: responseData.reason || "",
            code: responseData.code || "",
            msg: responseData.msg || responseData.message || "",
            message: responseData.message || responseData.msg || "",
            ...(Number.isFinite(Number(responseData.limit)) ? { limit: Number(responseData.limit) } : {}),
            ...(Number.isFinite(Number(responseData.currentCount)) ? { currentCount: Number(responseData.currentCount) } : {})
        };
    }

    const responseProspect = read_contact_display_payload(responseData?.prospect || responseData);
    const prospect = responseProspect?._id ? responseProspect : false;

    if (prospect && responseData?.sync) {
        prospect.sync = responseData.sync;
    }

    return prospect;
};

/**
 * Reads an old Client identity only while legacy Brand snapshots are being retired.
 */
const read_legacy_brand_client_id = (brand) => {
    return `${brand?.clientId || brand?.clientLink?.clientId || brand?.clientLink?.mongoId || ""}`.trim();
};

/**
 * Reads imported-field metadata from the canonical field with old nested fallback support.
 */
const read_brand_imported_fields = (brand) => {
    return brand?.importedFields || brand?.clientLink?.importedFields || {};
};

/**
 * Reads stable active/inactive Quest totals from one canonical Brand.
 */
export const read_brand_quest_counts = (brand = {}) => {
    return read_quest_provider_counts(brand);
};

/**
 * Keeps the row's saved Brand record focused on canonical fields.
 */
const read_canonical_brand_record = (brand) => {
    const importedFields = read_brand_imported_fields(brand);
    const brandRecord = { ...(brand || {}) };

    delete brandRecord.sellerId;
    delete brandRecord.assignee;
    delete brandRecord.prospectStatus;
    delete brandRecord.brand;
    delete brandRecord.brands;
    delete brandRecord.phone;
    delete brandRecord.phoneList;
    delete brandRecord.email;
    delete brandRecord.emailList;
    delete brandRecord.clientLink;

    return {
        ...brandRecord,
        clientId: read_legacy_brand_client_id(brand) || undefined,
        importedFields
    };
};

/**
 * Reads the canonical seller from a row with legacy alias fallbacks.
 */
const read_brand_row_seller = (row) => {
    return row?.brandRecord?.seller || row?.seller || row?.assignee || row?.sellerId || null;
};

/**
 * Reads the canonical Sales status from a row with legacy alias fallbacks.
 */
const read_brand_row_status = (row) => {
    return row?.status || row?.prospectStatus || row?.brandRecord?.status || "Backlog";
};

/**
 * Keeps an existing client lifecycle status while applying local Sales-row updates.
 */
const read_brand_row_last_status_with_sales_status = (row, status) => {
    return read_client_label_after_sales_update(row, status);
};

/**
 * Reads or creates the current Sales-board entry timestamp.
 */
const read_sales_entered_at = (row, inSales) => {
    const currentSalesEnteredAt = row?.salesEnteredAt || row?.brandRecord?.salesEnteredAt || "";

    if (!inSales) {
        return "";
    }

    if (currentSalesEnteredAt) {
        return currentSalesEnteredAt;
    }

    return new Date(Date.now()).toISOString();
};

/**
 * Reads whether one seller payload contains a stable identity value.
 */
const read_has_seller_value = (seller) => {
    if (typeof seller === "string") {
        return Boolean(seller.trim());
    }

    return Boolean(seller?.id || seller?.userId || seller?.username || seller?.email || seller?.name);
};

/**
 * Reads whether a Brand row belongs on the Sales board.
 */
export const read_brand_in_sales = (brand) => {
    const status = brand?.status || brand?.prospectStatus || brand?.brandRecord?.status || prospectBacklogStatus;

    if (brand?.inSales === true || brand?.brandRecord?.inSales === true) {
        return true;
    }

    if (brand?.inSales === false || brand?.brandRecord?.inSales === false) {
        return false;
    }

    if (status === prospectBacklogStatus) {
        return false;
    }

    return read_has_seller_value(brand?.seller || brand?.assignee || brand?.sellerId || brand?.brandRecord?.seller);
};

/**
 * Reads the status used after a Brand leaves the Sales board.
 */
const read_status_after_sales_toggle_off = () => {
    return prospectBacklogStatus;
};

/**
 * Reads a unique list of clean text values for contacts.
 */
export const read_prospect_unique_values = (...fields) => {
    const values = fields.flatMap((field) => Array.isArray(field) ? field : [field]).filter(Boolean);

    return values.map((value) => `${value || ""}`.trim()).filter(Boolean).filter((value, index, list) => list.indexOf(value) === index);
};

/**
 * Reads an editable row field without falling back after the user clears it.
 */
const read_editable_row_value = (row, rowField, recordField) => {
    if (Object.prototype.hasOwnProperty.call(row || {}, rowField)) {
        return row?.[rowField] || "";
    }

    return row?.brandRecord?.[recordField] || "";
};

/**
 * Reads contact arrays without re-adding stale brandRecord values after deletes.
 */
const read_editable_contact_values = (row, scalarField, listField, valuesField) => {
    if (Object.prototype.hasOwnProperty.call(row || {}, valuesField)) {
        return read_prospect_unique_values(row?.[valuesField]);
    }

    if (Object.prototype.hasOwnProperty.call(row || {}, scalarField) || Object.prototype.hasOwnProperty.call(row || {}, listField)) {
        return read_prospect_unique_values(row?.[scalarField], row?.[listField]);
    }

    return read_prospect_unique_values(row?.brandRecord?.[valuesField]);
};

/**
 * Maps one brand comment into the legacy action shape used by the comments UI.
 */
const read_comment_action = (comment) => {
    return {
        _id: comment?._id,
        id: comment?._id,
        action: "Note",
        comment: comment?.comment || "",
        seller: {
            userId: comment?.author?.userId || "",
            name: comment?.author?.name || comment?.author?.firstname || "",
            avatarUrl: comment?.author?.avatarUrl || ""
        },
        prospectStatus: comment?.status || "",
        leadStatus: comment?.status || "",
        createdAt: comment?.createdAt,
        updatedAt: comment?.updatedAt
    };
};

/**
 * Maps brand history into action-like rows needed by schedule and history UI.
 */
const read_history_action = (history) => {
    if (["status", "sales_final_status"].includes(history?.type)) {
        return null;
    }

    const finalStatusTitle = prospectFinalSalesStatuses.includes(history?.to?.status) ? history.to.status : "";
    const legacyActionTitle = history?.type === "legacy_action" ? history?.to?.action : "";
    const salesSummaryTitle = history?.type === "sales_day_summary" ? "Sales day summary" : "";
    const historyType = legacyActionTitle || salesSummaryTitle || finalStatusTitle || {
        calendar: "New Call",
        offer: "Offer Created",
        status: finalStatusTitle,
        seller: "Seller Assigned",
        seller_removed: "Seller Removed",
        brand_update: "Brand Update",
        sales_day_summary: salesSummaryTitle,
        legacy_tag: "Automated Tag",
        legacy_seller: "Legacy Seller",
        legacy_status: "Legacy Status",
        legacy_action: legacyActionTitle
    }[history?.type] || "History";

    return {
        _id: history?._id,
        id: history?._id,
        action: historyType,
        schedule: history?.to?.schedule || false,
        comment: history?.note || "",
        seller: {
            userId: history?.to?.seller?.userId || history?.by?.userId || "",
            name: history?.to?.seller?.name || history?.to?.seller?.username || history?.by?.name || ""
        },
        prospectStatus: history?.to?.status || "",
        leadStatus: history?.to?.status || "",
        createdAt: history?.createdAt,
        updatedAt: history?.updatedAt
    };
};

/**
 * Sorts action rows by their saved timeline timestamp.
 */
const read_sorted_actions = (actions) => {
    return [...actions].sort((firstAction, secondAction) => {
        const firstDate = new Date(firstAction?.timestamp || firstAction?.createdAt || firstAction?.created || firstAction?.updatedAt || 0);
        const secondDate = new Date(secondAction?.timestamp || secondAction?.createdAt || secondAction?.created || secondAction?.updatedAt || 0);

        return firstDate.getTime() - secondDate.getTime();
    });
};

/**
 * Removes duplicated legacy Note rows after comments and history are merged.
 */
const read_unique_actions = (actions) => {
    const seenActions = new Set();

    return actions.filter((action) => {
        const actionKey = [
            action?.action || "",
            action?.comment || "",
            action?.createdAt || action?.created || ""
        ].join("|");

        if (seenActions.has(actionKey)) {
            return false;
        }

        seenActions.add(actionKey);

        return true;
    });
};

/**
 * Maps one canonical timeline row into the legacy action shape.
 */
const read_timeline_action = (timelineRow) => {
    const timelineMessage = timelineRow?.message || timelineRow?.note || timelineRow?.comment || "";
    const actionTitle = timelineRow?.type === "comment" ? "Note" : timelineMessage || timelineRow?.type || "Timeline";
    const actor = timelineRow?.owner || timelineRow?.by || {};
    const timestamp = timelineRow?.timestamp || timelineRow?.changedAt || timelineRow?.createdAt || timelineRow?.updatedAt;

    return {
        _id: timelineRow?.sourceId || timelineRow?.sourceKey || timelineRow?._id || timelineRow?.id,
        id: timelineRow?.sourceId || timelineRow?.sourceKey || timelineRow?._id || timelineRow?.id,
        action: actionTitle,
        comment: timelineRow?.type === "comment" ? timelineMessage : "",
        seller: {
            userId: actor?.userId || actor?.id || actor?._id || "",
            name: actor?.name || actor?.firstname || actor?.username || actor?.email || "",
            avatarUrl: actor?.avatarUrl || actor?.picture || ""
        },
        prospectStatus: timelineRow?.type === "status" ? timelineMessage : "",
        leadStatus: timelineRow?.type === "status" ? timelineMessage : "",
        timestamp,
        createdAt: timestamp,
        updatedAt: timestamp
    };
};

/**
 * Converts one nested prospect brand into the current frontend brand-row shape.
 */
export const read_prospect_brand_row = (prospect, brand) => {
    prospect = read_contact_display_payload(prospect);
    brand = read_contact_display_payload(brand);
    const comments = (brand?.comments || []).map((comment) => read_comment_action(comment));
    const historyActions = (brand?.history || []).map((history) => read_history_action(history)).filter(Boolean);
    const timeline = (brand?.timeline || []).length ? brand.timeline : brand?.legacy_timeline || [];
    const timelineActions = timeline.map((timelineRow) => read_timeline_action(timelineRow));
    const source = brand?.source || prospect?.source || "";
    const actions = read_unique_actions(read_sorted_actions([...timelineActions, ...historyActions, ...comments]));
    const importedFields = read_brand_imported_fields(brand);
    const questCounts = read_brand_quest_counts(brand);
    const brandAliasValue = Array.isArray(brand?.brands) ? brand.brands[0] : brand?.brands;
    const brandName = brand?.name || brand?.brand || brandAliasValue || "";
    const actualBusinessName = brand?.actualBusinessName || brand?.businessName || "";
    const seller = brand?.seller || brand?.assignee || brand?.sellerId || null;
    const status = brand?.status || brand?.prospectStatus || "Backlog";
    const lastStatus = brand?.lastStatus || status;
    const inSales = read_brand_in_sales({
        ...(brand || {}),
        seller,
        status
    });
    const phones = brand?.phones?.length ? brand.phones : read_prospect_unique_values(brand?.phone, brand?.phoneList);
    const emails = brand?.emails?.length ? brand.emails : read_prospect_unique_values(brand?.email, brand?.emailList);
    const brandRecord = read_canonical_brand_record({
        ...(brand || {}),
        name: brandName,
        seller,
        status,
        inSales,
        phones,
        emails,
        importedFields
    });

    return {
        _id: brand?._id,
        id: `${prospect?._id}-${brand?._id}`,
        prospectId: prospect?._id,
        brandId: brand?._id,
        prospect,
        brandRecord,
        contactAccess: brand?.contactAccess,
        firstName: prospect?.firstName || "",
        lastName: prospect?.lastName || "",
        firstname: brand?.ownerFirstName || prospect?.firstName || "",
        lastname: brand?.ownerLastName || prospect?.lastName || "",
        username: prospect?.username || "",
        registrationStatus: prospect?.registrationStatus || "unregistered",
        source,
        submittedBy: source,
        submittedByName: source,
        platformSource: source,
        brand: brandName,
        brands: [brandName].filter(Boolean),
        actualBusinessName,
        category: brand?.category || "",
        subcategory: brand?.subcategory || "",
        location: brand?.location || "",
        sellerId: seller,
        assignee: seller,
        seller,
        phone: phones?.[0] || "",
        phones,
        phoneList: phones,
        email: emails?.[0] || "",
        emails,
        emailList: emails,
        paidStatus: brand?.paidStatus || "none",
        status,
        lastStatus,
        inSales,
        prospectStatus: status,
        comments: brand?.comments || [],
        histories: brand?.history || [],
        legacy_timeline: brand?.legacy_timeline || [],
        timeline,
        actions,
        legacyLeadId: brand?.legacyLeadId || "",
        legacyProspectId: brand?.legacyProspectId || "",
        legacyBrandId: brand?.legacyBrandId || "",
        legacyCreatedAt: brand?.legacyCreatedAt || prospect?.legacyCreatedAt || "",
        salesEnteredAt: brand?.salesEnteredAt || "",
        createdAt: brand?.legacyCreatedAt || brand?.createdAt || prospect?.legacyCreatedAt || prospect?.createdAt,
        updatedAt: brand?.updatedAt || prospect?.updatedAt,
        ofrid: brand?.ofrid || "",
        offer: brand?.offer || null,
        clientId: read_legacy_brand_client_id(brand),
        clientCode: brand?.clientCode || brand?.difCode || brand?.clientLink?.difCode || "",
        questCounts,
        activeQuestCount: questCounts.active,
        inactiveQuestCount: questCounts.inactive,
        connectionSyncState: brand?.connectionSyncState || {},
        activeSalesSession: brand?.activeSalesSession || null,
        importedFields
    };
};

/**
 * Updates one nested Brand on a prospect while leaving the rest of the lead intact.
 */
export const read_prospect_with_brand_update = (prospect, brandId, brandUpdate) => {
    return {
        ...(prospect || {}),
        brands: (prospect?.brands || []).map((brand) => {
            const sameBrand = `${brand?._id || ""}` === `${brandId || ""}`;

            if (!sameBrand) {
                return brand;
            }

            return {
                ...brand,
                ...(brandUpdate || {})
            };
        })
    };
};

/**
 * Applies a seller change to the canonical Brand record, then derives row aliases.
 */
export const read_brand_row_with_seller = (row, sellerPayload, status) => {
    const nextStatus = status || read_brand_row_status(row);
    const lastStatus = read_brand_row_last_status_with_sales_status(row, nextStatus);
    const inSales = nextStatus !== prospectBacklogStatus;
    const brandId = row?.brandId || row?._id;
    const brandRecord = {
        ...(row?.brandRecord || {}),
        seller: sellerPayload,
        status: nextStatus,
        lastStatus,
        inSales,
        salesEnteredAt: read_sales_entered_at(row, inSales)
    };
    const prospect = read_prospect_with_brand_update(row?.prospect, brandId, {
        seller: sellerPayload,
        status: nextStatus,
        lastStatus,
        inSales,
        salesEnteredAt: brandRecord.salesEnteredAt
    });

    return {
        ...(row || {}),
        prospect,
        brandRecord,
        inSales,
        assignee: brandRecord.seller,
        seller: brandRecord.seller,
        sellerId: brandRecord.seller,
        salesEnteredAt: brandRecord.salesEnteredAt,
        status: brandRecord.status,
        lastStatus: brandRecord.lastStatus,
        prospectStatus: brandRecord.status
    };
};

/**
 * Applies a Sales membership toggle while keeping seller derived from the flag.
 */
export const read_brand_row_with_sales_toggle = (row, inSales) => {
    const nextStatus = inSales ? read_brand_row_status(row) : read_status_after_sales_toggle_off(read_brand_row_status(row));
    const status = inSales && nextStatus === prospectBacklogStatus ? prospectAssignedStatus : nextStatus;
    const seller = inSales ? read_brand_row_seller(row) : null;
    const lastStatus = read_brand_row_last_status_with_sales_status(row, status);
    const brandId = row?.brandId || row?._id;
    const brandRecord = {
        ...(row?.brandRecord || {}),
        inSales: Boolean(inSales),
        seller,
        status,
        lastStatus,
        salesEnteredAt: read_sales_entered_at(row, Boolean(inSales))
    };
    const prospect = read_prospect_with_brand_update(row?.prospect, brandId, {
        inSales: Boolean(inSales),
        seller,
        status,
        lastStatus,
        salesEnteredAt: brandRecord.salesEnteredAt
    });

    return {
        ...(row || {}),
        prospect,
        brandRecord,
        inSales: Boolean(inSales),
        assignee: brandRecord.seller,
        seller: brandRecord.seller,
        sellerId: brandRecord.seller,
        salesEnteredAt: brandRecord.salesEnteredAt,
        status: brandRecord.status,
        lastStatus: brandRecord.lastStatus,
        prospectStatus: brandRecord.status
    };
};

/**
 * Removes duplicate brand rows before board state stores them.
 */
const read_unique_prospect_brand_rows = (rows) => {
    const seenRowKeys = new Set();

    return rows.filter((row) => {
        const rowKey = `${row?.brandId || row?._id || row?.id || ""}`.trim();

        if (!rowKey) {
            return true;
        }

        if (seenRowKeys.has(rowKey)) {
            return false;
        }

        seenRowKeys.add(rowKey);
        return true;
    });
};

/**
 * Converts a prospect page into one frontend row per brand.
 */
export const read_prospect_brand_rows = (prospects, assignedOnly = false) => {
    const rows = read_unique_prospect_brand_rows((prospects || []).flatMap((prospect) => {
        return (prospect?.brands || []).map((brand) => read_prospect_brand_row(prospect, brand));
    }));

    if (!assignedOnly) {
        return rows;
    }

    return rows.filter((row) => read_brand_in_sales(row));
};

/**
 * Reads one lean brand payload from the current sales-row shape.
 */
export const read_brand_payload_from_row = (row) => {
    const inSales = read_brand_in_sales(row);
    const brandName = read_brand_input_value(row?.brand || row?.brandRecord?.internalName || row?.brandRecord?.name || "");

    return {
        _id: row?.brandId || row?._id,
        contactAccess: row?.contactAccess,
        ownerFirstName: read_editable_row_value(row, "firstname", "ownerFirstName"),
        ownerLastName: read_editable_row_value(row, "lastname", "ownerLastName"),
        name: brandName,
        internalName: brandName,
        category: row?.category || row?.brandRecord?.category || "",
        subcategory: row?.subcategory || row?.brandRecord?.subcategory || "",
        location: row?.location || row?.brandRecord?.location || "",
        seller: read_brand_row_seller(row),
        phones: read_editable_contact_values(row, "phone", "phoneList", "phones"),
        emails: read_editable_contact_values(row, "email", "emailList", "emails").map((email) => email.toLowerCase()),
        paidStatus: row?.paidStatus || row?.brandRecord?.paidStatus || "none",
        status: read_brand_row_status(row),
        inSales,
        salesEnteredAt: inSales ? row?.salesEnteredAt || row?.brandRecord?.salesEnteredAt || null : null,
        ofrid: row?.ofrid || row?.brandRecord?.ofrid || "",
        legacyLeadId: row?.legacyLeadId || row?.brandRecord?.legacyLeadId || "",
        legacyProspectId: row?.legacyProspectId || row?.brandRecord?.legacyProspectId || "",
        legacyBrandId: row?.legacyBrandId || row?.brandRecord?.legacyBrandId || ""
    };
};

/**
 * Fetches paginated prospects from the new Prospect collection.
 */
const get_prospects = (pageNo, pageSize, filters = {}) => {
    const url = `${config.NIFFLER_BACKEND_URL}/prospects/getProspects`;
    const data = {
        pageNo,
        pageSize,
        filters
    };

    return axios.post(url, data, {
        timeout: pageRequestTimeout
    })
        .then((res) => res.data)
        .catch((error) => {
            console.error("[Leads API] error", url, error?.response?.status, error?.response?.statusText, error?.message, error?.response?.data);
            throw error;
        });
};

/**
 * Fetches database-backed values for the Leads filter slider.
 */
const get_prospect_filter_options = () => {
    const url = `${config.NIFFLER_BACKEND_URL}/prospects/getProspectFilterOptions`;

    return axios.get(url, { timeout: requestTimeout })
        .then((res) => res.data || {})
        .catch((error) => {
            console.error("[Leads API] error", url, error?.response?.status, error?.response?.statusText, error?.message, error?.response?.data);
            return { sources: [] };
        });
};

/**
 * Fetches database-backed values for the Brands filter slider.
 */
const get_brand_filter_options = () => {
    const url = `${config.NIFFLER_BACKEND_URL}/prospects/getBrandFilterOptions`;

    return axios.get(url, { timeout: requestTimeout })
        .then((res) => res.data || {})
        .catch((error) => {
            console.error("[Brands API] error", url, error?.response?.status, error?.response?.statusText, error?.message, error?.response?.data);
            throw error;
        });
};

/**
 * Fetches prospects that have at least one brand assigned to Sales.
 */
const get_sales_prospects = (pageNo, pageSize, sellerKeys = []) => {
    const url = `${config.NIFFLER_BACKEND_URL}/prospects/getSalesProspects`;
    const data = {
        pageNo,
        pageSize,
        sellerKeys
    };

    return new Promise((resolve, reject) => {
        axios.post(url, data, {
            timeout: requestTimeout
        })
            .then((res) => {
                resolve(read_contact_display_payload(res.data));
            })
            .catch((error) => {
                console.error("[Leads API] error", url, error?.response?.status, error?.response?.statusText, error?.message, error?.response?.data);
                resolve({
                    prospects: [],
                    count: 0,
                    timeout: error?.code === "ECONNABORTED"
                });
            });
    });
};

/**
 * Searches prospects by names, emails, and phones.
 */
const search_prospects = (search, filters = {}) => {
    const url = `${config.NIFFLER_BACKEND_URL}/prospects/searchProspects`;
    const params = {
        search,
        sources: (filters?.sources || []).join(','),
        registrationStatus: filters?.registrationStatus || '',
        withPhone: Boolean(filters?.withPhone),
        withEmail: Boolean(filters?.withEmail),
        questProviders: (filters?.questProviders || []).join(','),
        difCodeBrands: filters?.difCodeBrands || 'all',
        sortBy: filters?.sortBy || 'leadCreatedAt',
        sortOrder: filters?.sortOrder || 'newest'
    };

    return new Promise((resolve, reject) => {
        axios.get(url, {
            params,
            timeout: searchRequestTimeout
        })
            .then((res) => {
                resolve(read_contact_display_payload(res.data?.prospects || []));
            })
            .catch((error) => {
                console.error("[Leads API] error", url, error?.response?.status, error?.response?.statusText, error?.message, error?.response?.data);
                reject(error);
            });
    });
};

/**
 * Searches Leads that can be selected as King merge candidates.
 */
const search_merge_prospects = (search, excludeId) => {
    const url = `${config.NIFFLER_BACKEND_URL}/prospects/searchMergeProspects`;
    const params = {
        search,
        excludeId
    };

    return new Promise((resolve, reject) => {
        axios.get(url, {
            params,
            timeout: searchRequestTimeout
        })
            .then((res) => {
                resolve(read_contact_display_payload(res.data?.prospects || []));
            })
            .catch((error) => {
                console.error("[Leads Merge API] error", url, error?.response?.status, error?.response?.statusText, error?.message, error?.response?.data);
                reject(error);
            });
    });
};

/**
 * Searches brand rows by names, emails, and phones from the database.
 */
export const read_brand_filter_params = (filters = {}) => ({
    brandStatuses: (filters?.brandStatuses || []).join(','),
    category: `${filters?.category || ''}`.trim(),
    subcategory: `${filters?.subcategory || ''}`.trim(),
    infoHas: (filters?.infoHas || []).join(','),
    infoMissing: (filters?.infoMissing || []).join(','),
    brandAutoTags: (filters?.brandAutoTags || []).join(','),
    questClientStatuses: (filters?.questClientStatuses || []).join(',')
});

/**
 * Normalizes counted Brand search responses with an old-endpoint fallback.
 */
export const read_brand_search_response = (responseData = {}) => {
    const prospects = read_contact_display_payload(responseData?.prospects || []);
    const responseCount = Number(responseData?.count);
    const returnedCount = read_prospect_brand_rows(prospects).length;

    return {
        prospects,
        count: responseData?.count !== undefined && Number.isFinite(responseCount)
            ? Math.max(responseCount, 0)
            : returnedCount
    };
};

const search_brand_prospects = (search, filters = {}) => {
    const url = `${config.NIFFLER_BACKEND_URL}/prospects/searchBrands`;
    const params = {
        search,
        ...read_brand_filter_params(filters)
    };
    const fallbackUrl = `${config.NIFFLER_BACKEND_URL}/prospects/searchProspects`;
    const filtersActive = Boolean(
        params.brandStatuses
        || params.category
        || params.subcategory
        || params.infoHas
        || params.infoMissing
        || params.brandAutoTags
        || params.questClientStatuses
    );

    return new Promise((resolve, reject) => {
        axios.get(url, {
            params,
            timeout: searchRequestTimeout
        })
            .then((res) => {
                resolve(read_brand_search_response(res.data));
            })
            .catch((error) => {
                console.error("[Brands API] error", url, error?.response?.status, error?.response?.statusText, error?.message, error?.response?.data);

                if (filtersActive || ![404, 405].includes(error?.response?.status)) {
                    reject(error);
                    return;
                }

                axios.get(fallbackUrl, {
                    params,
                    timeout: searchRequestTimeout
                })
                    .then((response) => {
                        resolve(read_brand_search_response(response.data));
                    })
                    .catch((fallbackError) => {
                        console.error("[Brands API] fallback error", fallbackUrl, fallbackError?.response?.status, fallbackError?.response?.statusText, fallbackError?.message, fallbackError?.response?.data);
                        reject(fallbackError);
                    });
            });
    });
};

/**
 * Requests whole activity cards from the Brand's stable timeline projection.
 */
const get_brand_timeline_page = (prospectId, brandId, cursor) => {
    const url = `${config.NIFFLER_BACKEND_URL}/prospects/brandTimeline`;

    return axios.get(url, {
        params: {
            prospectId,
            brandId,
            cursor,
            contract: 3,
            view: "activity"
        },
        timeout: requestTimeout
    }).then((response) => {
        return response.data?.data || {};
    }).catch((error) => {
        const timelineError = new Error(error?.response?.data?.message || error?.message || "Timeline unavailable");

        timelineError.retryable = Boolean(error?.response?.data?.retryable);
        timelineError.code = error?.response?.data?.code || error?.response?.data?.data?.code;
        timelineError.integration = error?.response?.data?.data?.integration || {};
        throw timelineError;
    });
};

const watch_brand_timeline = (prospectId, brandId, revision, signal) => {
    return axios.get(`${config.NIFFLER_BACKEND_URL}/prospects/brandTimeline/watch`, {
        params: { prospectId, brandId, revision }, signal, timeout: 35000
    }).then(response => response.data.data);
};

/**
 * Creates one Prospect document with nested brand data.
 */
const add_prospect = (prospect, actions) => {
    return new Promise((resolve, reject) => {
        run_with_shared_contact_confirmation(sharedContactsConfirmed => post_with_concurrent_confirmation(`${config.NIFFLER_BACKEND_URL}/prospects/addProspect`, {
            data: prospect,
            actions,
            sharedContactsConfirmed
        }))
            .then((res) => resolve(res))
            .catch((error) => reject(error));
    });
};

/**
 * Saves the full Prospect document from the Leads brand slider.
 */
const edit_prospect = (prospect) => {
    return new Promise((resolve, reject) => {
        post_with_concurrent_confirmation(`${config.NIFFLER_BACKEND_URL}/prospects/editProspect`, {
            data: read_contact_patch(prospect)
        })
            .then((res) => resolve(res))
            .catch((error) => reject(error));
    });
};

/**
 * Merges two normalized Prospect Leads through the King-only backend route.
 */
const merge_prospects = (selectedLeadId, candidateLeadId) => {
    return new Promise((resolve, reject) => {
        axios.post(`${config.NIFFLER_BACKEND_URL}/prospects/mergeProspects`, {
            selectedLeadId,
            candidateLeadId
        })
            .then((res) => resolve(res.data))
            .catch((error) => reject(error));
    });
};

/**
 * Saves one nested brand without touching the legacy Lead collection.
 */
const edit_prospect_brand = (row) => {
    const url = `${config.NIFFLER_BACKEND_URL}/prospects/editBrand`;
    const brand = read_contact_patch(read_brand_payload_from_row(row));
    const requestPayload = {
        prospectId: row?.prospectId,
        brandId: row?.brandId || row?._id,
        brand
    };

    console.log("[Niffler Sales API] editBrand request", read_sales_api_debug_payload(row, brand));

    return new Promise((resolve, reject) => {
        post_with_concurrent_confirmation(url, requestPayload)
            .then((res) => {
                console.log("[Niffler Sales API] editBrand success", read_sales_api_response_payload(res.data));
                resolve(read_prospect_response(res.data));
            })
            .catch((error) => {
                if (!error.cancelled) console.error("[Niffler Sales API] editBrand failed", read_sales_api_error_payload(error), read_sales_api_debug_payload(row, brand));
                reject(error);
            });
    });
};

/**
 * Deletes one saved Prospect brand when the lead still has another brand.
 */
const delete_prospect_brand = (prospect, brand) => {
    return new Promise((resolve, reject) => {
        axios.post(`${config.NIFFLER_BACKEND_URL}/prospects/deleteBrand`, {
            prospectId: prospect?._id || brand?.prospectId,
            brandId: brand?._id || brand?.brandId
        })
            .then((res) => resolve(res.data))
            .catch((error) => reject(error));
    });
};

/**
 * Assigns a seller to one brand and moves it into Sales To Call.
 */
const assign_brand_seller = (row, seller, status = prospectAssignedStatus) => {
    return new Promise((resolve, reject) => {
        post_with_concurrent_confirmation(`${config.NIFFLER_BACKEND_URL}/prospects/assignBrandSeller`, {
            prospectId: row?.prospectId,
            brandId: row?.brandId || row?._id,
            seller,
            status
        })
            .then((res) => resolve(read_prospect_response(res.data)))
            .catch((error) => reject(error));
    });
};

/**
 * Reads role-scoped Sales assignment, call, and outcome performance for the selected period.
 */
const get_sales_performance = (startDate = "", endDate = "", options = {}) => {
    const cacheKey = `${startDate || ""}:${endDate || ""}`;
    const cachedResponse = salesPerformanceCache.get(cacheKey);
    const now = Date.now();

    if (cachedResponse?.data && cachedResponse.expiresAt > now && options.force !== true) {
        return Promise.resolve(cachedResponse.data);
    }

    return axios.get(`${config.NIFFLER_BACKEND_URL}/prospects/getSalesPerformance`, {
        params: {
            ...(startDate ? { startDate } : {}),
            ...(endDate ? { endDate } : {})
        },
        ...(cachedResponse?.etag ? { headers: { "If-None-Match": cachedResponse.etag } } : {}),
        ...(options.signal ? { signal: options.signal } : {}),
        validateStatus: (status) => (status >= 200 && status < 300) || status === 304
    }).then((response) => {
        if (response.status === 304 && cachedResponse?.data) {
            salesPerformanceCache.set(cacheKey, {
                ...cachedResponse,
                expiresAt: Date.now() + salesPerformanceCacheTtl
            });
            return cachedResponse.data;
        }

        const data = response.data || {};

        salesPerformanceCache.set(cacheKey, {
            data,
            etag: response.headers?.etag || response.headers?.ETag || "",
            expiresAt: Date.now() + salesPerformanceCacheTtl
        });

        return data;
    });
};

/**
 * Reads one cursor-paginated page of the first assignments behind the assignment KPI.
 */
const get_sales_performance_assignments = (startDate = "", endDate = "", sellerKey = "", cursor = "", options = {}) => {
    return axios.get(`${config.NIFFLER_BACKEND_URL}/prospects/getSalesPerformanceAssignments`, {
        params: {
            ...(startDate ? { startDate } : {}),
            ...(endDate ? { endDate } : {}),
            ...(sellerKey ? { sellerKey } : {}),
            ...(cursor ? { cursor } : {}),
            limit: options.limit || 20
        },
        ...(options.signal ? { signal: options.signal } : {})
    }).then((response) => {
        const data = response.data || {};

        return {
            assignments: Array.isArray(data.assignments) ? data.assignments : [],
            hasMore: data.hasMore === true,
            nextCursor: `${data.nextCursor || ""}`,
            total: Number(data.total || 0)
        };
    });
};

/**
 * Reads one cursor-paginated page of completed updates behind the update-duration KPI.
 */
const get_sales_performance_updates = (startDate = "", endDate = "", sellerKey = "", cursor = "", options = {}) => {
    return axios.get(`${config.NIFFLER_BACKEND_URL}/prospects/getSalesPerformanceUpdates`, {
        params: {
            ...(startDate ? { startDate } : {}),
            ...(endDate ? { endDate } : {}),
            ...(sellerKey ? { sellerKey } : {}),
            ...(cursor ? { cursor } : {}),
            limit: options.limit || 20
        },
        ...(options.signal ? { signal: options.signal } : {})
    }).then((response) => {
        const data = response.data || {};

        return {
            updates: Array.isArray(data.updates) ? data.updates : [],
            hasMore: data.hasMore === true,
            nextCursor: `${data.nextCursor || ""}`,
            total: Number(data.total || 0)
        };
    });
};

/**
 * Updates the Sales board status of one brand.
 */
const update_brand_status = (row, status, brand, action) => {
    const url = `${config.NIFFLER_BACKEND_URL}/prospects/updateBrandStatus`;
    const brandPayload = brand ? read_brand_payload_from_row(brand) : null;
    const requestPayload = {
        prospectId: row?.prospectId,
        brandId: row?.brandId || row?._id,
        status,
        action,
        brand: brandPayload
    };

    console.log("[Niffler Sales API] updateBrandStatus request", {
        ...read_sales_api_debug_payload(row, brandPayload || {}),
        nextStatus: status || "",
        action: action || ""
    });

    return new Promise((resolve, reject) => {
        post_with_concurrent_confirmation(url, requestPayload)
            .then((res) => {
                console.log("[Niffler Sales API] updateBrandStatus success", read_sales_api_response_payload(res.data));
                resolve(read_prospect_response(res.data));
            })
            .catch((error) => {
                if (!error.cancelled) console.error("[Niffler Sales API] updateBrandStatus failed", read_sales_api_error_payload(error), {
                    ...read_sales_api_debug_payload(row, brandPayload || {}),
                    nextStatus: status || "",
                    action: action || ""
                });
                reject(error);
            });
    });
};

/**
 * Toggles whether one Brand stays on the Sales board.
 */
const toggle_brand_sales = (row, inSales) => {
    return new Promise((resolve, reject) => {
        axios.post(`${config.NIFFLER_BACKEND_URL}/prospects/toggleBrandSales`, {
            prospectId: row?.prospectId,
            brandId: row?.brandId || row?._id,
            inSales
        })
            .then((res) => resolve(read_prospect_response(res.data)))
            .catch((error) => reject(error));
    });
};

/**
 * Adds a rich-text comment directly to one brand.
 */
const create_brand_comment = (row, comment, status) => {
    const commentId = window.crypto?.randomUUID?.() || `comment-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    return new Promise((resolve, reject) => {
        axios.post(`${config.NIFFLER_BACKEND_URL}/prospects/createBrandComment`, {
            prospectId: row?.prospectId,
            brandId: row?.brandId || row?._id,
            comment,
            status,
            commentId
        })
            .then((res) => resolve(res.data))
            .catch((error) => reject(error));
    });
};

/**
 * Creates a calendar callback for one brand and moves it to Stalling.
 */
const create_brand_calendar = (calendar) => {
    return new Promise((resolve, reject) => {
        axios.post(`${config.NIFFLER_BACKEND_URL}/prospects/createBrandCalendar`, {
            prospectId: calendar?.prospectId,
            brandId: calendar?.brandId,
            schedule: calendar?.schedule,
            seller: calendar?.seller,
            comment: calendar?.comment
        })
            .then((res) => resolve(res.data))
            .catch((error) => reject(error));
    });
};

/**
 * Starts a Godfather offer export for one prospect brand.
 */
const create_offer_from_brand = (row) => {
    const url = `${config.NIFFLER_BACKEND_URL}/prospects/createOffer`;
    const requestPayload = {
        prospectId: row?.prospectId,
        brandId: row?.brandId || row?._id
    };

    console.log("[Niffler Sales API] createOffer request", read_sales_api_debug_payload(row, row?.brandRecord || {}));

    return new Promise((resolve, reject) => {
        axios.post(url, requestPayload)
            .then((res) => {
                console.log("[Niffler Sales API] createOffer success", read_sales_api_response_payload(res.data));
                resolve(read_contact_display_payload(res.data));
            })
            .catch((error) => {
                console.error("[Niffler Sales API] createOffer failed", read_sales_api_error_payload(error), read_sales_api_debug_payload(row, row?.brandRecord || {}));
                reject(error);
            });
    });
};

/**
 * Completes the SASS handoff for one prospect brand and closes Sales as Sass.
 */
const create_sass_from_brand = (row, meetingDate, seller, caller) => {
    return new Promise((resolve, reject) => {
        axios.post(`${config.NIFFLER_BACKEND_URL}/prospects/createSass`, {
            prospectId: row?.prospectId,
            brandId: row?.brandId || row?._id,
            meetingDate,
            seller,
            caller,
            contractSeller: caller
        })
            .then((res) => resolve(res.data))
            .catch((error) => reject(error));
    });
};

/**
 * Toggles the temporary own-ad-account flag on one prospect lead.
 */
const toggle_prospect_own_ad_account = (prospectId, status) => {
    return new Promise((resolve, reject) => {
        axios.post(`${config.NIFFLER_BACKEND_URL}/prospects/toggleOwnAdAccount`, {
            prospectId,
            status
        })
            .then((res) => resolve(res))
            .catch((error) => reject(error));
    });
};

export {
    get_prospects,
    get_prospect_filter_options,
    get_brand_filter_options,
    get_sales_prospects,
    get_sales_performance,
    get_sales_performance_assignments,
    get_sales_performance_updates,
    search_prospects,
    search_merge_prospects,
    search_brand_prospects,
    get_brand_timeline_page,
    watch_brand_timeline,
    add_prospect,
    edit_prospect,
    merge_prospects,
    edit_prospect_brand,
    delete_prospect_brand,
    assign_brand_seller,
    update_brand_status,
    toggle_brand_sales,
    create_brand_comment,
    create_brand_calendar,
    create_offer_from_brand,
    create_sass_from_brand,
    toggle_prospect_own_ad_account
};
